/**
 * Typed client for /admin-retention/* — backs the Retention screen.
 *
 * RetentionOffer = a winback bonus mechanic record. Created by:
 *   - Stage C cron (retentionWinbackJob) for silent engaged users
 *   - Desktop Stage E (Windows MSI uninstall intercept, when wired)
 *
 * Status lifecycle:
 *   offered → accepted → vested  (happy path, +bonus credited)
 *   offered → declined           (user rejected, 6mo cooldown set)
 *   offered → expired            (silence after offered, never claimed)
 *   accepted → expired           (silence during 7d vest window, no credit)
 */
import { client } from './client';

export type RetentionStatus =
  | 'offered'
  | 'accepted'
  | 'vested'
  | 'expired'
  | 'declined';

export interface PublisherRef {
  _id: string;
  name?: string;
  email?: string;
  companyName?: string;
}

export interface AppRef {
  _id: string;
  name?: string;
  appKey?: string;
  currencyName?: string;
}

export interface AppUserRef {
  _id: string;
  userKey?: string;
  publisherUserId?: string;
  installedAt?: string;
  lastSeen?: string;
}

export interface RetentionOffer {
  _id: string;
  appUserId?: AppUserRef | string | null;
  personId: string;
  publisherId?: PublisherRef | string | null;
  appId?: AppRef | string | null;
  bonusAmount: number;
  status: RetentionStatus;
  vestAt?: string | null;
  vestedAt?: string | null;
  declinedAt?: string | null;
  eligibilitySnapshot?: Record<string, unknown>;
  internalNotes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface RetentionListResponse {
  docs: RetentionOffer[];
  totalDocs: number;
  limit: number;
  page: number;
  totalPages: number;
  hasNextPage?: boolean;
  hasPrevPage?: boolean;
}

export interface RetentionStatsResponse {
  byStatus: Partial<Record<RetentionStatus, number>>;
  last24h: number;
  last7d: number;
  totalBonusVested: number;
  acceptRate: number;
}

export interface RetentionListParams {
  page?: number;
  limit?: number;
  status?: RetentionStatus | '';
  publisherId?: string;
  sort?: string;
}

export interface RetentionUpdateInput {
  id: string;
  status?: RetentionStatus;
  internalNotes?: string;
}

// ─── Fetchers ─────────────────────────────────────────────────────────────

export async function fetchRetention(
  params: RetentionListParams
): Promise<RetentionListResponse> {
  const cleanParams: Record<string, unknown> = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v !== '' && v !== undefined && v !== null) {
      cleanParams[k] = v;
    }
  });
  const r = await client.get<RetentionListResponse>('/admin-retention', {
    params: cleanParams
  });
  return r.data;
}

export async function fetchRetentionStats(): Promise<RetentionStatsResponse> {
  const r = await client.get<RetentionStatsResponse>('/admin-retention/stats');
  return r.data;
}

export async function fetchRetentionOne(id: string): Promise<RetentionOffer> {
  const r = await client.get<RetentionOffer>(`/admin-retention/${id}`);
  return r.data;
}

export async function updateRetention(
  input: RetentionUpdateInput
): Promise<RetentionOffer> {
  const { id, ...body } = input;
  const r = await client.patch<RetentionOffer>(`/admin-retention/${id}`, body);
  return r.data;
}
